import { AssetType, Quote } from '@tradingblock/types';
import { AggregatedPositionDTO, PositionDTO, PartialQuote } from '@tradingblock/api';
import _ from 'lodash';
import { positionToPerformance } from '../data/global/selectors/transforms';

export function avgPositionStrat(positions: (PositionDTO | AggregatedPositionDTO)[]): number {
  if (!positions || positions.length === 0) {
    return 0;
  }

  // Separate stocks and options
  const stockPositions = positions.filter(p => p.AssetType === AssetType.Equity);
  const optionPositions = positions.filter(p => p.AssetType === AssetType.Option);

  // Calculate stock positions
  let stockCostBasis = 0;
  let stockQuantity = 0;
  for (const position of stockPositions) {
    const { OpenPrice, OpenQuantity } = position;
    stockCostBasis += OpenPrice * OpenQuantity;
    stockQuantity += OpenQuantity;
  }

  // Calculate options positions
  let optionsCostBasis = 0;
  let optionsQuantity = 0;
  for (const position of optionPositions) {
    const { OpenPrice, OpenQuantity } = position;
    // Don't multiply by OptionMultiplier here since it will be applied later in calculatePositionCostValue
    optionsCostBasis += OpenPrice * OpenQuantity;
    optionsQuantity += OpenQuantity;
  }

  // If we have both stocks and options, we need to keep them separate
  if (stockPositions.length > 0 && optionPositions.length > 0) {
    // Return stock average if we're looking at stocks, options average if looking at options
    // Guard against division by zero
    if (positions[0].AssetType === AssetType.Equity) {
      return stockQuantity !== 0 ? stockCostBasis / stockQuantity : 0;
    } else {
      return optionsQuantity !== 0 ? optionsCostBasis / optionsQuantity : 0;
    }
  }

  // If we only have one type, return its average
  const totalCostBasis = stockCostBasis + optionsCostBasis;
  const totalQuantity = stockQuantity + optionsQuantity;

  if (totalQuantity === 0) {
    return 0;
  }

  return totalCostBasis / totalQuantity;
}

/**
 * Calculates the position cost value based on the average price, the number of contracts and the multiplier (100 - option | 1 - share).
 * @param avgPrice - number | the average price
 * @param numOfContracts - number | the number of contracts
 * @param assetType - AssetType | the asset type
 * @param multiplier - number (optional) | the multiplier
 * @returns - number | the position cost value
 */
export const calculatePositionCostValue = (
  avgPrice: number,
  numOfContracts: number,
  assetType: AssetType,
  multiplier?: number
): number => {
  // We are setting another multiplier based on the asset type to create a fallback in the case the multiplier is not available from the backend.
  let multiplierFromAssetType = 1;

  if (assetType === AssetType.Option) {
    multiplierFromAssetType = 100;
  }

  let value = avgPrice * numOfContracts * (multiplier || multiplierFromAssetType);

  if (Number.isNaN(value)) {
    return 0;
  } else {
    return value;
  }
};

export function calculateTotalPositionCost(positions: (PositionDTO | AggregatedPositionDTO)[]): number {
  if (!positions || positions.length === 0) {
    return 0;
  }

  if (positions.length === 1) {
    return calculatePositionCostValue(
      avgPositionStrat(positions),
      positions[0].OpenQuantity,
      positions[0].AssetType,
      positions[0].OptionMultiplier
    );
  }

  // Always calculate cost without commission, regardless of symbol
  const stockPositions = positions.filter(p => p.AssetType === AssetType.Equity);
  const optionPositions = positions.filter(p => p.AssetType === AssetType.Option);

  // Calculate stock cost - handle each position separately
  const stockCost = stockPositions.reduce((sum, position) => {
    return (
      sum +
      calculatePositionCostValue(
        position.OpenPrice,
        position.OpenQuantity,
        position.AssetType,
        position.OptionMultiplier
      )
    );
  }, 0);

  // Calculate options cost
  const optionsCost = optionPositions.reduce((sum, position) => {
    // For options, use the actual sign of OpenQuantity to handle spreads correctly
    return (
      sum +
      calculatePositionCostValue(
        position.OpenPrice,
        position.OpenQuantity, // Remove Math.abs() to preserve the sign
        position.AssetType,
        position.OptionMultiplier
      )
    );
  }, 0);

  return stockCost + optionsCost;
}

export const calculatePositionGain = (
  position: AggregatedPositionDTO,
  feedQuote: PartialQuote,
  avgPositionStratFn: Function
): number => {
  if (position.UseCalculatedPnL) {
    return position.OverallPnl || 0;
  } else {
    const costValue = calculatePositionCostValue(
      avgPositionStratFn([position]),
      position.OpenQuantity,
      position.AssetType,
      position.OptionMultiplier
    );

    const performance = positionToPerformance(position, feedQuote);
    const value = performance.value;

    return value - costValue;
  }
};
